/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { useWindowDimensions } from "../../utils/Hooks";

import DataTable from "../../components/table/DataTable";
import { GridRenderCellParams } from "@mui/x-data-grid";

import DateIntervalSelector from "../../components/controls/DateIntervalSelector";
import { useMaterialReportAggregate } from "./materialsReportsHooks";

const MaterialsProfitList = () => {
    const { height } = useWindowDimensions();

    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();

    const { data, status } = useMaterialReportAggregate(startDate ?? "", endDate ?? "");

    let columns: any[] = [
        {
            field: "name",
            width: 250,
            headerName: "Part/Material",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.name;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 245 }}>
                        {`${params.row.name ?? ""}`}
                    </Typography>
                );
            },
        },
        {
            field: "manufacturer",
            width: 150,
            headerName: "Manufacturer",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.manufacturer;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {`${params.row.manufacturer ?? ""}`}
                    </Typography>
                );
            },
        },
        {
            field: "part_number",
            width: 150,
            headerName: "Part #",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.part_number;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {`${params.row.part_number ?? ""}`}
                    </Typography>
                );
            },
        },
        {
            field: "units_sold",
            width: 120,
            headerName: "Units Sold",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.units_sold;
            },
            renderCell: (params: GridRenderCellParams) => {
                // NOTE: Price is managed on the backend, this is actually `acquisition_price`.
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 95 }}>
                        {`${params.row.units_sold.toLocaleString()}`}
                    </Typography>
                );
            },
        },
        {
            field: "value",
            width: 150,
            headerName: "List Price",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.list_price;
            },
            renderCell: (params: GridRenderCellParams) => {
                // NOTE: Price is managed on the backend, this is actually `acquisition_price`.
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 95 }}>
                        {`$${params.row.list_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                    </Typography>
                );
            },
        },
        {
            field: "acquisition_price",
            width: 150,
            headerName: "Acquisition Price",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.acquisition_price;
            },
            renderCell: (params: GridRenderCellParams) => {
                // NOTE: Price is managed on the backend, this is actually `acquisition_price`.
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 95 }}>
                        {`$${params.row.acquisition_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                    </Typography>
                );
            },
        },
        {
            field: "retail_price",
            width: 150,
            headerName: "Sale Price",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.retail_price;
            },
            renderCell: (params: GridRenderCellParams) => {
                // NOTE: Price is managed on the backend, this is actually `acquisition_price`.
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 95 }}>
                        {`$${params.row.retail_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                    </Typography>
                );
            },
        },
        {
            field: "profit",
            width: 150,
            headerName: "Profit",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.retail_price - params.row.acquisition_price;
            },
            renderCell: (params: GridRenderCellParams) => {
                // NOTE: Price is managed on the backend, this is actually `acquisition_price`.
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 95 }}>
                        {`$${(params.row.retail_price - params.row.acquisition_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                    </Typography>
                );
            },
        },
    ];

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Materials", route: "/?section=materials" },
                    { text: "Reports", route: "/materials/reports" },
                ]}
            />

            <Box sx={{ mt: 1, mb: 3 }}>
                <DateIntervalSelector
                    onDatesSelected={(startDate: string, endDate: string) => {
                        setStartDate(startDate);
                        setEndDate(endDate);
                    }}
                />
            </Box>

            {status === "pending" && (
                <Stack direction="row" justifyContent="center">
                    <CircularProgress size={40} sx={{ mt: 15, mb: 5 }} />
                </Stack>
            )}

            {status !== "pending" && (
                <Card sx={{ mt: 1 }}>
                    <CardContent>
                        <DataTable
                            tableName="MATERIALS_REPORTS"
                            data={data?.results ?? []}
                            allowRowSelected={false}
                            columns={columns}
                            height={height - 250}
                        />
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default MaterialsProfitList;
